import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { DataItem, navigateTo } from 'scrivito'

import { StringText } from '@/Atomic/Atoms/StringText'
import { Button } from '@/Atomic/Molecules/Button'
import InfoBox from '@/Atomic/Molecules/InfoBox'
import { CustomField } from '@/Atomic/Organisms/CustomField'
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from '@/Atomic/Templates/Drawer'
import { Form, FormBody, FormFields, FormGroup } from '@/Atomic/Templates/Form'
import { errorToast } from '@/Components/Toast'
import { Activity } from '@/Data/Activity/ActivityDataClass'
import { useDataClassFormHandler } from '@/utils/CRUD/hooks/useDataClassFormHandler'
import { getDataClassFormMap } from '@/utils/CRUD/read/dataClass'

interface DataClassActivityDrawerProps {
  isOpen: boolean
  close: () => void
  onCreated?: (newActivity: DataItem) => void | Promise<void>
  linkedEntityName?: string | null
}

export const DataClassActivityDrawer: React.FC<
  DataClassActivityDrawerProps
> = ({ isOpen, close, onCreated, linkedEntityName }) => {
  const { t } = useTranslation()

  const { methods } = useDataClassFormHandler(Activity, {
    prefill: { enabled: isOpen },
  })
  const {
    getValues,
    watch,
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods
  const groups = getDataClassFormMap(Activity, getValues, watch)

  const onDrawerClose = () => {
    reset()
    close()
  }

  const handleCreate = handleSubmit(async (data) => {
    try {
      const newActivity = await Activity.create(data)

      if (onCreated) {
        await onCreated(newActivity)
      } else {
        navigateTo(newActivity)
      }
      reset()
    } catch (error) {
      errorToast(
        error instanceof Error
          ? error
          : new Error(t('errors.unknownError.headline')),
      )
    }
  })

  return (
    <Drawer isOpen={isOpen} close={onDrawerClose} direction="right">
      <DrawerHeader>
        {t('components.activity.newActivity')}
        <Button
          variant="icon"
          icon="cross"
          tooltip={t('tooltip.close')}
          onClick={onDrawerClose}
        />
      </DrawerHeader>
      <DrawerBody>
        <FormProvider {...methods}>
          <Form onSubmit={handleCreate}>
            <FormBody>
              {linkedEntityName && (
                <InfoBox
                  iconPosition="side"
                  text={t('components.activity.linkedTo', {
                    entityName: linkedEntityName,
                  })}
                />
              )}
              {groups.map((group) => (
                <FormGroup key={group.name}>
                  <StringText variant="title">{group.title}</StringText>
                  <FormFields>
                    {group.fields.map((field) => (
                      <CustomField
                        key={field.fieldKey}
                        fieldMap={field}
                        isEditing={true}
                        className="form-field"
                      />
                    ))}
                  </FormFields>
                </FormGroup>
              ))}
            </FormBody>
          </Form>
        </FormProvider>
      </DrawerBody>
      <DrawerFooter align="right">
        <Button variant="outlined" onClick={onDrawerClose} size="lg">
          {t('common.cancel')}
        </Button>
        <Button
          icon="disk"
          onClick={handleCreate}
          size="lg"
          disabled={isSubmitting}
        >
          {t('common.save')}
        </Button>
      </DrawerFooter>
    </Drawer>
  )
}
